import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Button from '@/components/base/Button';
import Tabs from '@/components/base/Tabs';
import Badge from '@/components/base/Badge';
import Avatar from '@/components/base/Avatar';
import StatusBadge from '@/components/base/StatusBadge';
import ProgressBar from '@/components/base/ProgressBar';
import PlatformIcon from '@/components/base/PlatformIcon';
import Switch from '@/components/base/Switch';
import EarlyPayoutNotice from '@/components/feature/EarlyPayoutNotice';
import DeliverablesPanel from './components/DeliverablesPanel';
import ContractsPanel from './components/ContractsPanel';
import ApplicantsPanel from './components/ApplicantsPanel';
import EditCampaignModal, { type CampaignEdits } from './components/EditCampaignModal';
import AddCreatorModal, { type AddCreatorInput } from './components/AddCreatorModal';
import RespondModal, { type RespondInput } from './components/RespondModal';
import EarlyPayoutRequests from './components/EarlyPayoutRequests';
import AiRecommendations from './components/AiRecommendations';
import PaymentsInsight from './components/PaymentsInsight';
import CreatorAiModal, { type Vetting } from './components/CreatorAiModal';
import Modal from '@/components/base/Modal';
import { useDeliverableReviews } from '@/hooks/useDeliverableReviews';
import { useRole } from '@/context/RoleContext';
// Tabs not yet ported to the API still read these mocks (docs/BACKEND_GAPS.md).
import { api, apiList, ApiError } from '@/utils/api';
import {
  loadApplicantStages,
  persistApplicantStages,
  type ApplicantStageMap,
} from '@/utils/applicantStore';
import { formatCompact, formatMoney, formatDate, formatDateTime } from '@/utils/format';
import { balanceAfterAdvance } from '@/utils/invoice';
import { isHireReady } from '@/utils/contract';
import type {
  ApplicantStage,
  Campaign,
  CampaignActivityItem,
  CampaignApplicant,
  CampaignContract,
  CampaignCreator,
  CampaignDeliverable,
  CampaignPerformance,
  EarlyPayoutRequest,
  Invoice,
  InvoiceAdvance,
} from '@/types/portal';

const tabs = [
  { id: 'overview', label: 'Overview', icon: 'ri-dashboard-line' },
  { id: 'creators', label: 'Creators', icon: 'ri-user-star-line' },
  { id: 'applications', label: 'Applications', icon: 'ri-inbox-archive-line' },
  { id: 'deliverables', label: 'Deliverables', icon: 'ri-movie-line' },
  { id: 'contracts', label: 'Contracts', icon: 'ri-file-paper-2-line' },
  { id: 'performance', label: 'Performance', icon: 'ri-line-chart-line' },
  { id: 'payment', label: 'Payment', icon: 'ri-wallet-3-line' },
];

/** The live portal's next-stage button, by the campaign's current stage (Laravel's advanceLifecycle). */
const NEXT_STAGE: Record<string, { label: string; to: string }> = {
  Recruiting: { label: 'Mark Active', to: 'Active' },
  Active: { label: 'Move to Review', to: 'Review' },
  Review: { label: 'Move to Paying', to: 'Paying' },
  Paying: { label: 'Mark Completed', to: 'Completed' },
};

type CampaignAction = 'advance' | 'disable' | 'enable' | 'archive' | 'reopen';

/** Statuses a brief (with its contract) can be sent from: invited, link opened, KYC done. */
const BRIEF_STATUSES = ['invited', 'clicked', 'kyc_completed'];
/** Creators who haven't opened or finished the invite yet, so it can be sent again. */
const INVITE_STATUSES = ['invited', 'clicked'];

/** Statuses the live portal offers "Mark active" on (creator-detail's canHireStatuses), plus Brief sent ("Hire creator"). */
const HIRE_STATUSES = ['brief_sent', 'clicked', 'kyc_completed', 'accepted', 'signed', 'contract_pending', 'contracted'];

/** Roster statuses once a creator is hired and working on the campaign. */
const WORKING_STATUSES = ['hired', 'active', 'deliverable_submitted', 'revision_requested', 'content_approved'];

/**
 * Whether a creator can be paid from this campaign now, and if not, why. Only
 * payable creators (all content approved) with KYC done and a wallet are eligible.
 */
function payEligibility(entry: CampaignCreator): { eligible: boolean; reason: string } {
  const creator = entry.creator;
  const status = entry.statusKey ?? '';
  const done = entry.deliverablesDone;
  const total = entry.deliverablesTotal;
  const count = total > 0 ? ` (${done} of ${total} approved)` : '';
  if (status === 'paid' || status === 'completed') return { eligible: false, reason: 'Already paid' };
  if (status === 'payment_processing') return { eligible: false, reason: 'Payment already in progress' };
  if (status !== 'payable') {
    if (status === 'deliverable_submitted') return { eligible: false, reason: `Content waiting for your approval${count}` };
    if (status === 'revision_requested') return { eligible: false, reason: `Changes requested on their content${count}` };
    if (status === 'content_approved') {
      return {
        eligible: false,
        reason: total > 0 && done < total ? `Not all content approved yet${count}` : 'Content approved, but not marked payable yet',
      };
    }
    if (WORKING_STATUSES.includes(status)) return { eligible: false, reason: `Content not submitted or approved yet${count}` };
    if (status === 'declined' || status === 'removed') return { eligible: false, reason: 'Not on the campaign any more' };
    return { eligible: false, reason: 'Not hired yet' };
  }
  if (creator?.kycStatus !== 'KYC complete') return { eligible: false, reason: 'KYC not complete' };
  if (creator?.walletStatus !== 'Wallet active') return { eligible: false, reason: 'No active wallet' };
  return { eligible: true, reason: 'All content approved' };
}

interface RosterConfirm {
  title: string;
  body: string;
  label: string;
  run: () => Promise<string>;
}

const activityIcon: Record<string, string> = {
  exception: 'ri-error-warning-line',
  approval: 'ri-checkbox-circle-line',
  contract: 'ri-file-paper-2-line',
  invite: 'ri-send-plane-line',
  alert: 'ri-alert-line',
};

export default function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useRole();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [roster, setRoster] = useState<CampaignCreator[]>([]);
  const [activity, setActivity] = useState<CampaignActivityItem[]>([]);
  const [deliverables, setDeliverables] = useState<CampaignDeliverable[]>([]);
  const [campaignContracts, setCampaignContracts] = useState<CampaignContract[]>([]);
  const [applicants, setApplicants] = useState<CampaignApplicant[]>([]);
  const [performance, setPerformance] = useState<CampaignPerformance | null>(null);
  const [applicantError, setApplicantError] = useState('');
  // "Under review" has no backend storage, so it is kept in this browser only.
  const [reviewingOverrides, setReviewingOverrides] = useState<ApplicantStageMap>(() =>
    loadApplicantStages(),
  );
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'notFound' | 'error'>('loading');
  const [loadError, setLoadError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get('tab') ?? 'overview');
  // Search results open ?tab=creators, also when this campaign is already open.
  const tabParam = searchParams.get('tab');
  useEffect(() => {
    if (tabParam) setTab(tabParam);
  }, [tabParam]);
  const [copied, setCopied] = useState(false);
  const [joinCopied, setJoinCopied] = useState(false);
  const [inviteOnly, setInviteOnly] = useState(true);
  const [inviteOnlyError, setInviteOnlyError] = useState('');
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editError, setEditError] = useState('');
  const [confirming, setConfirming] = useState<CampaignAction | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState('');
  const [notice, setNotice] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [addError, setAddError] = useState('');
  const [respondEntry, setRespondEntry] = useState<CampaignCreator | null>(null);
  // The live creator page's AI cards (vet, outreach draft), opened from a roster row.
  const [aiEntry, setAiEntry] = useState<{ entry: CampaignCreator; mode: 'vet' | 'outreach' } | null>(null);
  const [vettings, setVettings] = useState<Record<string, Vetting>>({});
  const [respondError, setRespondError] = useState('');
  const [rosterConfirm, setRosterConfirm] = useState<RosterConfirm | null>(null);
  const [rosterError, setRosterError] = useState('');
  const [earlyPayouts, setEarlyPayouts] = useState<EarlyPayoutRequest[]>([]);
  const [reviewingId, setReviewingId] = useState<string | null>(null);
  // Payment tab: payable creators ticked for "Mark as processing", and the row being marked paid.
  const [processingIds, setProcessingIds] = useState<string[]>([]);
  const [payingId, setPayingId] = useState<string | null>(null);
  const canViewInvoices = can('invoices.view');

  // Invoices carry the early payout advances shown on the Payment tab. Loaded on
  // their own so staff without invoices.view still get the rest of the page.
  useEffect(() => {
    if (!canViewInvoices) return;
    let cancelled = false;
    apiList<Invoice>('/invoices')
      .then(({ data }) => {
        if (!cancelled) setInvoices(data);
      })
      .catch(() => {
        if (!cancelled) setInvoices([]);
      });
    return () => {
      cancelled = true;
    };
  }, [canViewInvoices, reloadKey]);

  // Early payout requests are loaded on their own, so a failure leaves the rest of the page.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    api<EarlyPayoutRequest[]>(`/campaigns/${id}/early-payouts`)
      .then((data) => {
        if (!cancelled) setEarlyPayouts(data ?? []);
      })
      .catch(() => {
        if (!cancelled) setEarlyPayouts([]);
      });
    return () => {
      cancelled = true;
    };
  }, [id, reloadKey]);

  // Campaign, roster and activity come from the API.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoadState('loading');
    Promise.all([
      api<Campaign>(`/campaigns/${id}`),
      apiList<CampaignCreator>(`/campaigns/${id}/creators`),
      api<CampaignActivityItem[]>(`/campaigns/${id}/activity`),
      api<CampaignDeliverable[]>(`/campaigns/${id}/contents`),
      api<CampaignContract[]>(`/campaigns/${id}/contracts`),
      api<CampaignApplicant[]>(`/campaigns/${id}/applications`),
      api<CampaignPerformance>(`/campaigns/${id}/performance`),
    ])
      .then(([detail, creators, feed, submissions, agreements, applications, results]) => {
        if (cancelled) return;
        setCampaign(detail);
        setRoster(creators.data);
        setActivity(feed);
        setDeliverables(submissions);
        setCampaignContracts(agreements);
        setApplicants(applications);
        setPerformance(results);
        setInviteOnly(detail.inviteOnly ?? true);
        setLoadState('ready');
      })
      .catch((error: ApiError) => {
        if (cancelled) return;
        if (error.status === 404) {
          setLoadState('notFound');
        } else {
          setLoadError(error.message);
          setLoadState('error');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [id, reloadKey]);

  // Saves straight away; flips back if the API refuses.
  const changeInviteOnly = async (next: boolean) => {
    setInviteOnly(next);
    setInviteOnlyError('');
    try {
      const updated = await api<Campaign>(`/campaigns/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ inviteOnly: next }),
      });
      setCampaign(updated);
    } catch (error) {
      setInviteOnly(!next);
      setInviteOnlyError(error instanceof Error ? error.message : 'Could not update the campaign.');
    }
  };

  const saveEdits = async (changes: Partial<CampaignEdits>) => {
    setBusy(true);
    setEditError('');
    try {
      const updated = await api<Campaign>(`/campaigns/${id}`, { method: 'PATCH', body: JSON.stringify(changes) });
      setCampaign(updated);
      setEditOpen(false);
      setNotice('Campaign updated.');
    } catch (error) {
      setEditError(error instanceof Error ? error.message : 'Could not save the campaign.');
    } finally {
      setBusy(false);
    }
  };

  // Next stage and the onboarding link switch: Laravel's advance-lifecycle, disable and enable.
  const runAction = async (action: CampaignAction) => {
    setBusy(true);
    setActionError('');
    try {
      const path = action === 'advance' ? 'advance-lifecycle' : action;
      const updated = await api<Campaign>(`/campaigns/${id}/${path}`, { method: 'POST' });
      setCampaign(updated);
      setNotice(
        {
          advance: `Campaign moved to ${updated.status}.`,
          disable: 'Onboarding link disabled.',
          enable: 'Onboarding link enabled.',
          archive: 'Campaign archived.',
          reopen: `Campaign reopened: it's back in ${updated.status}.`,
        }[action],
      );
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Could not update the campaign.');
    } finally {
      setBusy(false);
      setConfirming(null);
    }
  };

  // A roster change also moves the campaign's counts, the activity feed and the applications list.
  const reloadRoster = async () => {
    const [detail, creators, feed, applications] = await Promise.all([
      api<Campaign>(`/campaigns/${id}`),
      apiList<CampaignCreator>(`/campaigns/${id}/creators`),
      api<CampaignActivityItem[]>(`/campaigns/${id}/activity`),
      api<CampaignApplicant[]>(`/campaigns/${id}/applications`),
    ]);
    setCampaign(detail);
    setRoster(creators.data);
    setActivity(feed);
    setApplicants(applications);
  };

  const addCreators = async (input: AddCreatorInput) => {
    setBusy(true);
    setAddError('');
    try {
      const result =
        input.mode === 'single'
          ? await api<{ added: number; duplicates: string[] }>(`/campaigns/${id}/creators`, {
              method: 'POST',
              body: JSON.stringify({ email: input.email, phone: input.phone, name: input.name }),
            })
          : await api<{ added: number; duplicates: string[] }>(`/campaigns/${id}/creators/bulk`, {
              method: 'POST',
              body: JSON.stringify({ csv: input.csv }),
            });
      setAddOpen(false);
      setNotice(
        input.mode === 'single'
          ? 'Creator added and notified.'
          : `${result.added} creator${result.added === 1 ? '' : 's'} added.${
              result.duplicates.length ? ` ${result.duplicates.length} already in the campaign (skipped).` : ''
            }`,
      );
      await reloadRoster();
    } catch (error) {
      setAddError(error instanceof Error ? error.message : 'Could not add the creator.');
    } finally {
      setBusy(false);
    }
  };

  const confirmRoster = async () => {
    if (!rosterConfirm) return;
    setBusy(true);
    setRosterError('');
    try {
      setNotice(await rosterConfirm.run());
      await reloadRoster();
    } catch (error) {
      setRosterError(error instanceof Error ? error.message : 'Could not update the creator.');
    } finally {
      setBusy(false);
      setRosterConfirm(null);
    }
  };

  const respond = async (input: RespondInput) => {
    if (!respondEntry) return;
    setBusy(true);
    setRespondError('');
    try {
      await api(`/campaigns/${id}/creators/${respondEntry.id}/respond`, { method: 'POST', body: JSON.stringify(input) });
      const name = respondEntry.creator?.name ?? 'The creator';
      setRespondEntry(null);
      setNotice(
        input.action === 'accepted'
          ? `${name}'s offer accepted.`
          : input.action === 'declined'
            ? `${name} declined.`
            : `Counter-offer sent to ${name}.`,
      );
      await reloadRoster();
    } catch (error) {
      setRespondError(error instanceof Error ? error.message : 'Could not send the response.');
    } finally {
      setBusy(false);
    }
  };

  const reviewEarlyPayout = async (request: EarlyPayoutRequest, approved: boolean, note: string) => {
    setReviewingId(request.id);
    setRosterError('');
    try {
      await api(`/campaigns/${id}/early-payouts/${request.id}/review`, {
        method: 'POST',
        body: JSON.stringify({ action: approved ? 'approved' : 'denied', reviewNotes: note, rosterId: request.rosterId }),
      });
      setNotice(`Early payout request ${approved ? 'approved' : 'declined'}.`);
      // Approved advances also change the invoices behind the balance-due column.
      setReloadKey((key) => key + 1);
    } catch (error) {
      setRosterError(error instanceof Error ? error.message : 'Could not review the request.');
    } finally {
      setReviewingId(null);
    }
  };

  // The live Payment tab's two steps: payable creators move to processing in bulk, then each is marked paid.
  const markProcessing = async () => {
    setBusy(true);
    setRosterError('');
    try {
      const { updated } = await api<{ updated: number }>(`/campaigns/${id}/bulk-mark-processing`, {
        method: 'POST',
        body: JSON.stringify({ creatorIds: processingIds }),
      });
      setProcessingIds([]);
      await reloadRoster();
      setNotice(`${updated} creator${updated === 1 ? '' : 's'} marked as processing.`);
    } catch (error) {
      setRosterError(error instanceof Error ? error.message : 'Could not mark the creators as processing.');
    } finally {
      setBusy(false);
    }
  };

  const markPaid = async (entry: CampaignCreator) => {
    setPayingId(entry.id);
    setRosterError('');
    try {
      await api(`/campaigns/${id}/creators/${entry.id}/mark-paid`, { method: 'POST' });
      await reloadRoster();
      setNotice(`${entry.creator?.name ?? 'The creator'} marked as paid.`);
    } catch (error) {
      setRosterError(error instanceof Error ? error.message : 'Could not mark the creator as paid.');
    } finally {
      setPayingId(null);
    }
  };

  // The local "Under review" mark only applies while the API still says New.
  const applicantStages = useMemo<ApplicantStageMap>(() => {
    const map: ApplicantStageMap = {};
    applicants.forEach((entry) => {
      if (entry.stage === 'New' && reviewingOverrides[entry.id] === 'Reviewing') {
        map[entry.id] = 'Reviewing';
      }
    });
    return map;
  }, [applicants, reviewingOverrides]);

  // Applicants still waiting on a decision (not yet hired or declined) drive the
  // Creators tab badge.
  const pendingApplicants = useMemo(
    () =>
      applicants.filter((entry) => {
        const stage = applicantStages[entry.id] ?? entry.stage;
        return stage === 'New' || stage === 'Reviewing';
      }).length,
    [applicants, applicantStages],
  );

  // The Applications tab is a jump link into the Creators view — once selected,
  // scroll the creator-facing applications section into view.
  useEffect(() => {
    if (tab !== 'applications') return;
    const section = document.getElementById('campaign-applications');
    if (section) section.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [tab]);

  // Hired sends the brief and Declined removes the creator (Laravel's actions), so the
  // roster, contracts and activity feed change too — reload them afterwards.
  const setApplicantStage = async (applicantId: string, stage: ApplicantStage) => {
    setApplicantError('');
    if (stage === 'Reviewing') {
      const applicant = applicants.find((entry) => entry.id === applicantId);
      if (applicant?.stage !== 'New') {
        setApplicantError(
          'Reconsidering a declined creator needs a re-invite, which is not supported by the backend yet.',
        );
        return;
      }
      setReviewingOverrides((prev) => {
        const next: ApplicantStageMap = { ...prev, [applicantId]: 'Reviewing' };
        persistApplicantStages(next);
        return next;
      });
      return;
    }
    try {
      // Production's routes: Hired sends the brief, Declined removes the creator.
      if (stage !== 'Hired' && stage !== 'Declined') {
        setApplicantError('That stage is not supported by the backend yet.');
        return;
      }
      const updated = await api<CampaignApplicant>(
        stage === 'Hired'
          ? `/campaigns/${id}/creators/${applicantId}/send-brief`
          : `/campaigns/${id}/creators/${applicantId}`,
        { method: stage === 'Hired' ? 'POST' : 'DELETE' },
      );
      setApplicants((prev) => prev.map((entry) => (entry.id === updated.id ? updated : entry)));
      const [creators, feed, agreements] = await Promise.all([
        apiList<CampaignCreator>(`/campaigns/${id}/creators`),
        api<CampaignActivityItem[]>(`/campaigns/${id}/activity`),
        api<CampaignContract[]>(`/campaigns/${id}/contracts`),
      ]);
      setRoster(creators.data);
      setActivity(feed);
      setCampaignContracts(agreements);
    } catch (error) {
      setApplicantError(error instanceof Error ? error.message : 'Could not update the application.');
    }
  };

  const hireApplicant = (applicant: CampaignApplicant) => setApplicantStage(applicant.id, 'Hired');

  // Early payout advances drawn against invoices on this campaign, keyed by creator.
  const advanceByCreator = useMemo(() => {
    const map: Record<string, InvoiceAdvance> = {};
    invoices.forEach((invoice) => {
      if (invoice.campaignId === id && invoice.advance) {
        map[invoice.creatorId] = invoice.advance;
      }
    });
    return map;
  }, [id, invoices]);

  const deliverableState = useDeliverableReviews(id, deliverables, setDeliverables);

  const approvedCount = useMemo(
    () =>
      deliverables.filter((entry) => {
        const status = deliverableState.statusOf(entry.id);
        return status === 'Approved' || status === 'Paid';
      }).length,
    [deliverables, deliverableState],
  );

  const contractByCreator = useMemo(() => {
    const map: Record<string, CampaignContract> = {};
    campaignContracts.forEach((entry) => {
      map[entry.creatorId] = entry;
    });
    return map;
  }, [campaignContracts]);

  const rosterCount = roster.filter((entry) => !['removed', 'declined'].includes(entry.statusKey ?? '')).length;

  if (loadState === 'loading') {
    return (
      <div className="flex items-center justify-center px-4 py-24 text-sm text-foreground-500">
        <i className="ri-loader-4-line mr-2 animate-spin" />
        Loading campaign…
      </div>
    );
  }

  if (loadState === 'error') {
    return (
      <div className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-6 md:py-8">
        <div className="flex flex-col items-center gap-3 rounded-lg border border-primary-200 bg-primary-50 py-12 text-center">
          <p className="text-sm text-primary-800">
            <i className="ri-error-warning-line mr-1" />
            {loadError}
          </p>
          <Button variant="outline" size="sm" icon="ri-refresh-line" onClick={() => setReloadKey((key) => key + 1)}>
            Try again
          </Button>
        </div>
      </div>
    );
  }

  if (loadState === 'notFound' || !campaign) {
    return (
      <div className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-6 md:py-8">
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
          <span className="w-12 h-12 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 text-xl">
            <i className="ri-megaphone-line" />
          </span>
          <h1 className="mt-3 font-heading text-lg font-semibold text-foreground-950">
            Campaign not found
          </h1>
          <div className="mt-4">
            <Button variant="outline" icon="ri-arrow-left-line" onClick={() => navigate('/campaigns')}>
              Back to campaigns
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const payable = roster.reduce((sum, entry) => sum + entry.rate, 0);
  const advancedTotal = roster.reduce((sum, entry) => {
    const advance = advanceByCreator[entry.creatorId];
    return sum + (advance ? Math.min(advance.amount, entry.rate) : 0);
  }, 0);
  const netPayable = payable - advancedTotal;
  const advancedCount = roster.filter((entry) => advanceByCreator[entry.creatorId]).length;

  const link = campaign.onboardingLink ?? '';
  const canManage = can('campaigns.manage');
  const canClose = can('campaigns.close');
  const archived = campaign.status === 'Archived';
  const nextStage = NEXT_STAGE[campaign.status];
  const canBrief = can('campaigns.brief');
  const canReviewPayouts = can('payments.manage');

  /** The live portal's actions for one roster row, by its raw status. */
  const rosterActions = (entry: CampaignCreator) => {
    const status = entry.statusKey ?? '';
    const name = entry.creator?.name ?? 'this creator';
    const actions: { label: string; icon: string; onClick: () => void }[] = [];
    // As the live creator page: Vet creator, and Draft message before the brief goes out.
    if (canBrief && !['removed', 'declined'].includes(status)) {
      actions.push({ label: 'Vet', icon: 'ri-sparkling-2-line', onClick: () => setAiEntry({ entry, mode: 'vet' }) });
    }
    if (canBrief && ['shortlisted', 'invited'].includes(status)) {
      actions.push({ label: 'Draft message', icon: 'ri-quill-pen-line', onClick: () => setAiEntry({ entry, mode: 'outreach' }) });
    }
    if (canBrief && status === 'negotiating') {
      actions.push({ label: 'Respond', icon: 'ri-chat-3-line', onClick: () => { setRespondError(''); setRespondEntry(entry); } });
    }
    // Invited creators get the brief, which carries the fee, terms and contract: they accept, then sign.
    if (canBrief && BRIEF_STATUSES.includes(status)) {
      actions.push({
        label: 'Send brief & contract',
        icon: 'ri-file-paper-2-line',
        onClick: () =>
          setRosterConfirm({
            title: `Send the brief to ${name}?`,
            body: `${name} gets the campaign brief with its fee, terms and contract. Once they accept it, they sign the contract and you can hire them.`,
            label: 'Send brief',
            run: async () => {
              await api(`/campaigns/${id}/creators/${entry.id}/send-brief`, { method: 'POST' });
              return `Brief and contract sent to ${name}.`;
            },
          }),
      });
    }
    if (canManage && INVITE_STATUSES.includes(status)) {
      actions.push({
        label: 'Resend invite',
        icon: 'ri-mail-send-line',
        onClick: () =>
          setRosterConfirm({
            title: `Resend the invite to ${name}?`,
            body: `${name} gets the campaign invite again by email, SMS and in the Twiva app.`,
            label: 'Resend invite',
            run: async () => {
              await api(`/campaigns/${id}/creators/${entry.id}/resend-invite`, { method: 'POST' });
              return `Invite resent to ${name}.`;
            },
          }),
      });
    }
    if (canBrief && HIRE_STATUSES.includes(status)) {
      actions.push({
        label: status === 'brief_sent' ? 'Hire' : 'Mark active',
        icon: 'ri-user-follow-line',
        onClick: () =>
          setRosterConfirm({
            title: `Hire ${name}?`,
            body: `${name} becomes active on this campaign and is told by email, SMS and in the Twiva app. They need a registered account and an active wallet.`,
            label: 'Hire',
            run: async () => {
              await api(`/campaigns/${id}/creators/${entry.id}/hire`, { method: 'POST' });
              return `${name} is now active on this campaign.`;
            },
          }),
      });
    }
    if (canManage && status === 'removed' && entry.contact?.email) {
      actions.push({
        label: 'Re-invite',
        icon: 'ri-user-shared-line',
        onClick: () =>
          setRosterConfirm({
            title: `Re-invite ${name}?`,
            body: `${name} is invited to the campaign again and gets a new invite link.`,
            label: 'Re-invite',
            run: async () => {
              await api(`/campaigns/${id}/creators`, {
                method: 'POST',
                body: JSON.stringify({ email: entry.contact?.email, phone: entry.contact?.phone ?? '', name: entry.contact?.name ?? '' }),
              });
              return `${name} re-invited.`;
            },
          }),
      });
    } else if (canManage && status !== 'removed') {
      actions.push({
        label: 'Remove',
        icon: 'ri-user-unfollow-line',
        onClick: () =>
          setRosterConfirm({
            title: `Remove ${name}?`,
            body: `${name} is taken off the campaign and told by email and SMS. You can re-invite them later.`,
            label: 'Remove',
            run: async () => {
              await api(`/campaigns/${id}/creators/${entry.id}`, { method: 'DELETE' });
              return `${name} removed from the campaign.`;
            },
          }),
      });
    }
    return actions;
  };
  const linkEnabled = campaign.linkEnabled !== false;
  // Laravel's join link (findActiveCampaign) also refuses invite-only and expired campaigns.
  const linkExpired = Boolean(campaign.linkExpiresOn && campaign.linkExpiresOn < new Date().toISOString().slice(0, 10));
  const linkBlockedReason = !linkEnabled
    ? ''
    : inviteOnly
      ? 'Invite only is on, so this link shows creators "no longer active". Turn off Invite only (top of the page) to let creators join through it.'
      : linkExpired
        ? 'This link has expired, so creators see "no longer active". Edit the campaign to set a later link expiry date.'
        : '';
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };
  // This portal's agency join page, opening on this campaign: creators sign up or sign in, then join.
  const joinPageLink = campaign.agencyJoinRef
    ? `${window.location.origin}${__BASE_PATH__.replace(/\/$/, '')}/join/${campaign.agencyJoinRef}?campaign=${campaign.onboardingToken ?? ''}`
    : '';
  const copyJoinPageLink = async () => {
    try {
      await navigator.clipboard.writeText(joinPageLink);
      setJoinCopied(true);
      window.setTimeout(() => setJoinCopied(false), 2000);
    } catch {
      setJoinCopied(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-6 md:py-8">
      <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
        <Link to="/campaigns" className="cursor-pointer hover:text-foreground-800">
          Campaigns
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-800">{campaign.name}</span>
      </nav>

      <div className="mt-4 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <span className="page-rule" aria-hidden="true" />
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
              {campaign.name}
            </h1>
            <StatusBadge status={campaign.status} />
            <div className="flex items-center gap-2 rounded-full border border-background-200 bg-background-50 py-1 pl-3 pr-2">
              <span className="w-4 h-4 flex items-center justify-center text-sm text-foreground-500">
                <i className={inviteOnly ? 'ri-lock-line' : 'ri-group-line'} />
              </span>
              <span className="text-xs font-label font-medium whitespace-nowrap text-foreground-700">
                {inviteOnly ? 'Invite only' : 'Open to matching creators'}
              </span>
              <Switch
                checked={inviteOnly}
                onChange={changeInviteOnly}
                label="Invite only"
                disabled={!canManage}
              />
            </div>
          </div>
          <p className="mt-1 text-sm text-foreground-600">
            {[campaign.brandClient, campaign.markets.join(', '), campaign.platforms.join(', ')]
              .filter(Boolean)
              .join(' · ')}
          </p>
          {inviteOnlyError ? (
            <p role="alert" className="mt-1 text-xs text-primary-700">
              <i className="ri-error-warning-line mr-1" />
              {inviteOnlyError}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2">
          {canManage ? (
            <Button variant="outline" icon="ri-edit-line" onClick={() => { setEditError(''); setEditOpen(true); }}>
              Edit
            </Button>
          ) : null}
          {canManage && nextStage ? (
            <Button variant="outline" icon="ri-arrow-right-line" onClick={() => setConfirming('advance')}>
              {nextStage.label}
            </Button>
          ) : null}
          {canManage && archived ? (
            <Button variant="outline" icon="ri-inbox-unarchive-line" onClick={() => setConfirming('reopen')}>
              Reopen
            </Button>
          ) : null}
          {canClose && !archived ? (
            <Button variant="outline" icon="ri-archive-line" onClick={() => setConfirming('archive')}>
              Archive
            </Button>
          ) : null}
          <Button
            variant="outline"
            icon="ri-user-add-line"
            onClick={() => navigate(`/discovery?campaign=${campaign.id}`)}
          >
            Invite creators
          </Button>
          <Button
            variant="outline"
            icon="ri-send-plane-line"
            onClick={() => navigate('/payments/send')}
          >
            Pay creator
          </Button>
          <Button icon="ri-wallet-3-line" onClick={() => setTab('payment')}>
            Prepare payment
          </Button>
        </div>
      </div>

      {notice ? (
        <div className="mt-4 flex items-center justify-between gap-3 rounded-md border border-accent-200 bg-accent-50 px-4 py-2.5">
          <span className="inline-flex items-center gap-2 text-sm text-accent-800">
            <i className="ri-checkbox-circle-line" />
            {notice}
          </span>
          <button type="button" onClick={() => setNotice('')} className="cursor-pointer text-accent-700 hover:text-accent-900" aria-label="Dismiss">
            <i className="ri-close-line" />
          </button>
        </div>
      ) : null}
      {actionError ? (
        <div role="alert" className="mt-4 flex items-center justify-between gap-3 rounded-md border border-primary-200 bg-primary-50 px-4 py-2.5">
          <span className="inline-flex items-center gap-2 text-sm text-primary-800">
            <i className="ri-error-warning-line" />
            {actionError}
          </span>
          <button type="button" onClick={() => setActionError('')} className="cursor-pointer text-primary-700 hover:text-primary-900" aria-label="Dismiss">
            <i className="ri-close-line" />
          </button>
        </div>
      ) : null}

      <div className="mt-6 overflow-x-auto pb-1">
        <Tabs
          items={tabs
            .filter((entry) => entry.id !== 'applications' || !inviteOnly)
            .map((entry) => {
              // Creators on the roster (not removed or declined), applicants waiting,
              // contracts sent (signed or awaiting signature).
              if (entry.id === 'creators') return { ...entry, count: rosterCount };
              if (entry.id === 'applications') return { ...entry, count: pendingApplicants };
              if (entry.id === 'deliverables') return { ...entry, count: deliverables.length };
              if (entry.id === 'contracts') {
                return { ...entry, count: campaignContracts.filter((contract) => contract.status !== 'Draft').length };
              }
              return entry;
            })}
          value={tab}
          onChange={setTab}
        />
      </div>

      {tab === 'overview' ? (
        <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_340px]">
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <p className="text-xs text-foreground-500">Budget</p>
                <p className="mt-1 font-heading text-lg font-semibold text-foreground-950">
                  {formatMoney(campaign.budget, campaign.currency)}
                </p>
                {campaign.budgetPerCreator ? (
                  <p className="mt-0.5 text-[11px] text-foreground-500">
                    {formatMoney(campaign.budgetPerCreator, campaign.currency)} per creator
                  </p>
                ) : null}
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <p className="text-xs text-foreground-500">Spend</p>
                <p className="mt-1 font-heading text-lg font-semibold text-foreground-950">
                  {formatMoney(campaign.spend, campaign.currency)}
                </p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <p className="text-xs text-foreground-500">Creators</p>
                <p className="mt-1 font-heading text-lg font-semibold text-foreground-950">
                  {campaign.creatorsActive}/{campaign.creatorsTotal || '—'}
                </p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <p className="text-xs text-foreground-500">Progress</p>
                <p className="mt-1 font-heading text-lg font-semibold text-foreground-950">
                  {campaign.progress}%
                </p>
              </div>
            </div>

            {!inviteOnly && pendingApplicants > 0 ? (
              <button
                type="button"
                onClick={() => setTab('applications')}
                className="flex w-full flex-col items-start justify-between gap-3 rounded-lg border border-accent-200 bg-accent-50 px-5 py-4 text-left cursor-pointer transition-colors hover:bg-accent-100 sm:flex-row sm:items-center"
              >
                <div className="flex items-start gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-500 text-background-50">
                    <i className="ri-inbox-archive-line" />
                  </span>
                  <div>
                    <p className="text-sm font-heading font-semibold text-accent-900">
                      {pendingApplicants} application{pendingApplicants === 1 ? '' : 's'} awaiting review
                    </p>
                    <p className="mt-0.5 max-w-xl text-xs text-accent-800">
                      Creators have expressed interest in this open campaign. Review their fit and
                      invite the ones you want to move forward.
                    </p>
                  </div>
                </div>
                <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-xs font-label font-semibold text-accent-900">
                  Review applications
                  <i className="ri-arrow-right-line" />
                </span>
              </button>
            ) : null}

            <section
              id="campaign-brief"
              className="scroll-mt-20 rounded-lg border border-background-200 bg-background-50 p-5"
            >
              <h2 className="font-heading text-sm font-semibold text-foreground-950">Brief</h2>
              {campaign.objective ? (
                <>
                  <p className="mt-3 text-[11px] font-label font-medium uppercase tracking-wider text-foreground-400">
                    Campaign Objective
                  </p>
                  <p className="mt-1 text-sm leading-relaxed text-foreground-600">
                    {campaign.objective}
                  </p>
                </>
              ) : null}
              <p className="mt-4 text-[11px] font-label font-medium uppercase tracking-wider text-foreground-400">
                Campaign Description
              </p>
              <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-foreground-600">
                {campaign.description}
              </p>
              {campaign.deliverables ? (
                <>
                  <p className="mt-4 text-[11px] font-label font-medium uppercase tracking-wider text-foreground-400">
                    Deliverables
                  </p>
                  <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-foreground-600">
                    {campaign.deliverables}
                  </p>
                </>
              ) : null}
              <div className="mt-4 flex flex-wrap gap-2 text-xs text-foreground-600">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-background-200 px-2.5 py-1">
                  <i className="ri-calendar-line" />
                  {formatDate(campaign.startDate)} – {formatDate(campaign.endDate)}
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-background-200 px-2.5 py-1">
                  <i className="ri-global-line" />
                  {campaign.markets.join(' · ')}
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-background-200 px-2.5 py-1">
                  <span className="inline-flex items-center gap-1.5">
                    {campaign.platforms.map((platform) => (
                      <PlatformIcon key={platform} platform={platform} className="text-[13px]" />
                    ))}
                  </span>
                  {campaign.platforms.join(' · ')}
                </span>
              </div>
            </section>

            <section className="rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="flex items-start gap-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-500 text-background-50">
                  <i className="ri-link" />
                </span>
                <div className="min-w-0 flex-1">
                  <h2 className="font-heading text-sm font-semibold text-foreground-950">
                    Onboarding link
                  </h2>
                  <p className="text-xs text-foreground-500">For creators outside Twiva</p>
                  <p className="mt-1 text-sm leading-relaxed text-foreground-600">
                    Re-share this link at any time — creators register or log in through it and are
                    enrolled automatically.
                  </p>
                  <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
                    <code className="min-w-0 flex-1 truncate rounded-md border border-background-300 bg-background-100 px-3 py-2 text-xs font-label text-foreground-700">
                      {link}
                    </code>
                    <Button
                      variant="accent"
                      size="sm"
                      icon={copied ? 'ri-check-line' : 'ri-file-copy-line'}
                      onClick={copyLink}
                      className="shrink-0"
                    >
                      {copied ? 'Copied' : 'Copy link'}
                    </Button>
                    {canManage ? (
                      <Button
                        variant="outline"
                        size="sm"
                        icon={linkEnabled ? 'ri-link-unlink-line' : 'ri-link-m'}
                        onClick={() => setConfirming(linkEnabled ? 'disable' : 'enable')}
                        className="shrink-0"
                      >
                        {linkEnabled ? 'Disable link' : 'Re-enable link'}
                      </Button>
                    ) : null}
                  </div>
                  {joinPageLink ? (
                    <div className="mt-3 rounded-md border border-background-200 bg-background-100/60 px-3 py-2">
                      <p className="text-xs font-medium text-foreground-800">Join page</p>
                      <p className="text-xs text-foreground-500">
                        Your agency's page, opening on this campaign. Creators create a Twiva account or sign in, then join this and your other open campaigns. Invite-only campaigns take only creators on the roster.
                      </p>
                      <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
                        <code className="min-w-0 flex-1 truncate rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-label text-foreground-700">
                          {joinPageLink}
                        </code>
                        <Button
                          variant="outline"
                          size="sm"
                          icon={joinCopied ? 'ri-check-line' : 'ri-file-copy-line'}
                          onClick={copyJoinPageLink}
                          className="shrink-0"
                        >
                          {joinCopied ? 'Copied' : 'Copy'}
                        </Button>
                        <a
                          href={joinPageLink}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md px-3 text-xs font-medium text-primary-700 hover:bg-background-200"
                        >
                          <i className="ri-external-link-line" />
                          Open
                        </a>
                      </div>
                    </div>
                  ) : null}
                  {!linkEnabled ? (
                    <p className="mt-2 flex items-center gap-1.5 text-xs text-primary-700">
                      <i className="ri-link-unlink-line" />
                      Link disabled — creators can't join through it until it is re-enabled.
                    </p>
                  ) : null}
                  {linkBlockedReason ? (
                    <p className="mt-2 flex items-start gap-1.5 text-xs text-primary-700">
                      <i className="ri-error-warning-line mt-0.5" />
                      {linkBlockedReason}
                    </p>
                  ) : null}
                  <p className="mt-2 flex items-center gap-1.5 text-[11px] text-foreground-500">
                    <span className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className={campaign.linkExpiresOn ? 'ri-time-line' : 'ri-infinity-line'} />
                    </span>
                    {campaign.linkExpiresOn
                      ? `Link expires on ${formatDate(campaign.linkExpiresOn)}`
                      : 'No expiry — this link stays active'}
                  </p>
                </div>
              </div>
            </section>

            {campaign.aiSummary ? (
              <section className="rounded-lg border border-accent-200 bg-accent-50 p-5">
                <div className="flex items-start gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-500 text-background-50">
                    <i className="ri-sparkling-2-line" />
                  </span>
                  <div>
                    <h2 className="font-heading text-sm font-semibold text-accent-900">
                      AI status summary
                    </h2>
                    <p className="mt-1 text-sm leading-relaxed text-accent-800">
                      {campaign.aiSummary}
                    </p>
                  </div>
                </div>
              </section>
            ) : null}
          </div>

          <div className="space-y-5">
            {canManage ? <AiRecommendations campaignId={campaign.id} /> : null}
            <section className="rounded-lg border border-background-200 bg-background-50">
              <header className="border-b border-background-200 px-5 py-3">
                <h2 className="font-heading text-sm font-semibold text-foreground-950">
                  Activity
                </h2>
              </header>
              <div className="divide-y divide-background-100">
                {activity.map((item) => (
                  <div key={item.id} className="flex items-start gap-3 px-5 py-3">
                    <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-600">
                      <i className={activityIcon[item.type] ?? 'ri-record-circle-line'} />
                    </span>
                    <div>
                      <p className="text-xs text-foreground-800">{item.label}</p>
                      {item.notes ? (
                        <p className="text-[11px] text-foreground-500">{item.notes}</p>
                      ) : null}
                      <p className="text-[11px] text-foreground-400">{formatDateTime(item.at)}</p>
                    </div>
                  </div>
                ))}
                {activity.length === 0 ? (
                  <p className="px-5 py-6 text-center text-xs text-foreground-500">No activity yet.</p>
                ) : null}
              </div>
            </section>

            {campaign.flags.length > 0 ? (
              <section className="rounded-lg border border-primary-200 bg-primary-50 p-5">
                <h2 className="inline-flex items-center gap-1.5 font-heading text-sm font-semibold text-primary-800">
                  <i className="ri-flag-line" />
                  Needs attention
                </h2>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {campaign.flags.map((flag) => (
                    <StatusBadge key={flag} status={flag} />
                  ))}
                </div>
              </section>
            ) : null}
          </div>
        </div>
      ) : null}

      {tab === 'creators' || tab === 'applications' ? (
        <div className="mt-5 space-y-5">
          {canManage || rosterError ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              {rosterError ? (
                <p role="alert" className="flex items-center gap-2 text-sm text-primary-800">
                  <i className="ri-error-warning-line" />
                  {rosterError}
                </p>
              ) : (
                <span />
              )}
              {canManage ? (
                <Button size="sm" icon="ri-user-add-line" onClick={() => { setAddError(''); setAddOpen(true); }}>
                  Add creators
                </Button>
              ) : null}
            </div>
          ) : null}
          <div className="overflow-x-auto rounded-lg border border-background-200 bg-background-50">
          <table className="row-accent w-full min-w-[960px] border-collapse text-left">
            <thead>
              <tr className="border-b border-background-200 text-[11px] font-label font-semibold uppercase tracking-wider text-foreground-400">
                <th className="px-5 py-3 font-semibold">Creator</th>
                <th className="px-3 py-3 font-semibold">Status</th>
                <th className="px-3 py-3 font-semibold">Contract</th>
                <th className="px-3 py-3 font-semibold">Deliverables</th>
                <th className="px-3 py-3 font-semibold">Fit</th>
                <th className="px-3 py-3 font-semibold">Rate</th>
                <th className="px-3 py-3 font-semibold">Flags</th>
                <th className="px-5 py-3 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {roster.map((entry) => {
                const creator = entry.creator;
                if (!creator) return null;
                const contractStatus =
                  entry.contractStatus ??
                  (isHireReady(contractByCreator[entry.creatorId]?.status)
                    ? 'Signed'
                    : contractByCreator[entry.creatorId]
                      ? 'Awaiting signature'
                      : 'Not sent');
                return (
                  <tr
                    key={entry.id}
                    onClick={() => navigate(`/creators/${creator.id}`)}
                    className="cursor-pointer border-b border-background-100 last:border-0 hover:bg-background-100"
                  >
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <Avatar src={creator.avatar} alt={creator.name} size="sm" />
                        <div>
                          <p className="text-sm font-medium text-foreground-950">{creator.name}</p>
                          <p className="text-xs text-foreground-500">{creator.handle}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <StatusBadge status={entry.status} />
                    </td>
                    <td className="px-3 py-3">
                      {contractStatus === 'Signed' ? (
                        <Badge tone="accent" icon="ri-check-line">
                          Signed
                        </Badge>
                      ) : (
                        <Badge tone="secondary" icon="ri-lock-2-line">
                          {contractStatus}
                        </Badge>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      {entry.deliverablesTotal > 0 ? (
                        <ProgressBar
                          value={(entry.deliverablesDone / entry.deliverablesTotal) * 100}
                          showLabel
                          className="w-28"
                        />
                      ) : (
                        <span className="text-xs text-foreground-400">Not started</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-sm font-medium text-accent-700">
                      {vettings[entry.id]?.score ? `${vettings[entry.id].score}/5` : (entry.fitScore ?? '—')}
                    </td>
                    <td className="px-3 py-3 text-sm text-foreground-700">
                      {formatMoney(entry.rate, entry.currency)}
                    </td>
                    <td className="px-3 py-3">
                      {entry.flags.length > 0 ? (
                        <Badge tone="primary" icon="ri-alert-line">
                          {entry.flags[0]}
                        </Badge>
                      ) : (
                        <span className="text-xs text-foreground-400">Clear</span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex justify-end gap-1.5" onClick={(event) => event.stopPropagation()}>
                        {rosterActions(entry).map((action) => (
                          <Button key={action.label} size="sm" variant="outline" icon={action.icon} onClick={action.onClick}>
                            {action.label}
                          </Button>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {roster.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-5 py-12 text-center text-sm text-foreground-500">
                    No creators assigned yet. Invite creators from Discovery to build the pool.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
          </div>

          {!inviteOnly ? (
            <div id="campaign-applications" className="scroll-mt-24">
              {applicantError ? (
                <p
                  role="alert"
                  className="mb-3 flex items-center gap-2 rounded-md border border-primary-200 bg-primary-50 px-4 py-2.5 text-sm text-primary-800"
                >
                  <i className="ri-error-warning-line" />
                  {applicantError}
                </p>
              ) : null}
              <ApplicantsPanel
                applicants={applicants}
                stages={applicantStages}
                onSetStage={setApplicantStage}
                onHire={hireApplicant}
                onViewContracts={() => setTab('contracts')}
              />
            </div>
          ) : (
            <div className="flex flex-col items-start justify-between gap-3 rounded-lg border border-dashed border-background-300 bg-background-100 px-5 py-4 sm:flex-row sm:items-center">
              <div className="flex items-start gap-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-background-200 text-foreground-600">
                  <i className="ri-lock-line" />
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-900">Invite-only campaign</p>
                  <p className="mt-0.5 max-w-xl text-xs text-foreground-500">
                    Creators can't apply while this campaign is invite-only. Switch it to open to
                    start collecting expressions of interest here.
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                icon="ri-group-line"
                className="shrink-0"
                disabled={!canManage}
                onClick={() => changeInviteOnly(false)}
              >
                Open to matching creators
              </Button>
            </div>
          )}
        </div>
      ) : null}

      {tab === 'deliverables' ? (
        <DeliverablesPanel
          deliverables={deliverables}
          state={deliverableState}
          canReview={can('campaigns.approve_content')}
        />
      ) : null}

      {tab === 'contracts' ? (
        <ContractsPanel campaign={campaign} contracts={campaignContracts} />
      ) : null}

      {tab === 'performance' ? (
        <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
          <section className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-sm font-semibold text-foreground-950">
              Delivery vs plan
            </h2>
            <div className="mt-4 space-y-4">
              <div>
                <div className="mb-1 flex items-center justify-between text-xs text-foreground-600">
                  <span>Reach delivered</span>
                  <span className="font-medium text-foreground-900">
                    {performance?.reachDeliveredPct != null ? `${performance.reachDeliveredPct}%` : '—'}
                  </span>
                </div>
                <ProgressBar value={Math.min(100, performance?.reachDeliveredPct ?? 0)} tone="primary" />
              </div>
              <div>
                <div className="mb-1 flex items-center justify-between text-xs text-foreground-600">
                  <span>Engagement vs benchmark</span>
                  <span
                    className={`font-medium ${
                      (performance?.engagementVsBenchmarkPct ?? 0) >= 0 ? 'text-accent-700' : 'text-primary-700'
                    }`}
                  >
                    {performance?.engagementVsBenchmarkPct != null
                      ? `${performance.engagementVsBenchmarkPct >= 0 ? '+' : ''}${performance.engagementVsBenchmarkPct}%`
                      : '—'}
                  </span>
                </div>
                <ProgressBar
                  value={
                    performance?.engagementVsBenchmarkPct != null
                      ? Math.min(100, Math.max(0, 50 + performance.engagementVsBenchmarkPct))
                      : 0
                  }
                  tone="accent"
                />
              </div>
              <div>
                <div className="mb-1 flex items-center justify-between text-xs text-foreground-600">
                  <span>Deliverables approved</span>
                  <span className="font-medium text-foreground-900">
                    {approvedCount}/{deliverables.length || '—'}
                  </span>
                </div>
                <ProgressBar
                  value={deliverables.length ? (approvedCount / deliverables.length) * 100 : 0}
                  tone="secondary"
                />
              </div>
            </div>
            <p className="mt-4 flex items-center gap-1.5 text-[11px] text-foreground-500">
              <i className="ri-refresh-line" />
              {performance && performance.contentWithData > 0
                ? `${formatCompact(performance.totals.reach)} reach · ${formatCompact(performance.totals.engagements)} engagements across ${performance.contentWithData} post${performance.contentWithData === 1 ? '' : 's'}${performance.lastSyncedAt ? ` · synced ${formatDateTime(performance.lastSyncedAt)}` : ''}`
                : 'No platform metrics yet — they sync once approved content is published.'}
            </p>
          </section>

          <section className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-sm font-semibold text-foreground-950">
              Top performers
            </h2>
            <div className="mt-3 divide-y divide-background-100">
              {(performance?.creators ?? []).slice(0, 4).map((entry) => (
                <div key={entry.creatorId} className="flex items-center justify-between gap-3 py-3">
                  <div className="flex items-center gap-3">
                    <Avatar src={entry.avatar} alt={entry.name} size="sm" />
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{entry.name}</p>
                      <p className="text-xs text-foreground-500">
                        {formatCompact(entry.reach)} reach
                        {entry.engagementRate != null ? ` · ${entry.engagementRate}% eng.` : ''}
                      </p>
                    </div>
                  </div>
                  <span className="text-right">
                    <span className="block font-heading text-sm font-semibold text-accent-700">
                      {formatCompact(entry.engagements)}
                    </span>
                    <span className="block text-[10px] text-foreground-400">engagements</span>
                  </span>
                </div>
              ))}
              {!performance || performance.creators.length === 0 ? (
                <p className="py-6 text-center text-sm text-foreground-500">
                  No performance data yet.
                </p>
              ) : null}
            </div>
          </section>
        </div>
      ) : null}

      {tab === 'payment' ? (
        <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_340px]">
          <div className="min-w-0 space-y-5">
          {rosterError ? (
            <p role="alert" className="flex items-center gap-2 text-sm text-primary-800">
              <i className="ri-error-warning-line" />
              {rosterError}
            </p>
          ) : null}
          {can('payments.view') ? <PaymentsInsight campaignId={campaign.id} /> : null}
          <EarlyPayoutRequests
            requests={earlyPayouts}
            canReview={canReviewPayouts}
            busyId={reviewingId}
            onReview={reviewEarlyPayout}
          />
          {canReviewPayouts && roster.some((entry) => entry.statusKey === 'payable') ? (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-secondary-200 bg-secondary-50 px-4 py-2.5">
              <p className="text-sm text-secondary-900">
                {processingIds.length > 0
                  ? `${processingIds.length} payable creator${processingIds.length === 1 ? '' : 's'} selected`
                  : 'Tick payable creators to mark them as processing once their payment is on its way.'}
              </p>
              <Button
                size="sm"
                icon="ri-loader-2-line"
                disabled={processingIds.length === 0 || busy}
                onClick={() => void markProcessing()}
              >
                Mark as processing
              </Button>
            </div>
          ) : null}
          <div className="overflow-x-auto rounded-lg border border-background-200 bg-background-50">
            <table className="row-accent w-full min-w-[880px] border-collapse text-left">
              <thead>
                <tr className="border-b border-background-200 text-[11px] font-label font-semibold uppercase tracking-wider text-foreground-400">
                  <th className="px-5 py-3 font-semibold">Creator</th>
                  <th className="px-3 py-3 font-semibold">Payment</th>
                  <th className="px-3 py-3 font-semibold">Deliverables</th>
                  <th className="px-3 py-3 font-semibold">Rate</th>
                  <th className="px-3 py-3 font-semibold">Balance due</th>
                  <th className="px-3 py-3 font-semibold">Eligible to pay</th>
                  <th className="px-5 py-3 text-right font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {roster.map((entry) => {
                  const creator = entry.creator;
                  if (!creator) return null;
                  const advance = advanceByCreator[entry.creatorId];
                  const balance = balanceAfterAdvance(entry.rate, advance);
                  const { eligible, reason } = payEligibility(entry);
                  const payable = entry.statusKey === 'payable';
                  const processing = entry.statusKey === 'payment_processing';
                  const paid = entry.statusKey === 'paid' || entry.statusKey === 'completed';
                  return (
                    <tr key={entry.id} className="border-b border-background-100 last:border-0">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          {canReviewPayouts && payable ? (
                            <input
                              type="checkbox"
                              aria-label={`Select ${creator.name}`}
                              checked={processingIds.includes(entry.id)}
                              onChange={(event) =>
                                setProcessingIds((ids) =>
                                  event.target.checked ? [...ids, entry.id] : ids.filter((value) => value !== entry.id),
                                )
                              }
                              className="h-4 w-4 cursor-pointer accent-primary-600"
                            />
                          ) : canReviewPayouts ? (
                            <span className="w-4" aria-hidden="true" />
                          ) : null}
                          <Avatar src={creator.avatar} alt={creator.name} size="sm" />
                          <span className="text-sm font-medium text-foreground-900">
                            {creator.name}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        {paid ? (
                          <Badge tone="accent" icon="ri-check-double-line">Paid</Badge>
                        ) : processing ? (
                          <Badge tone="secondary" icon="ri-loader-2-line">Processing</Badge>
                        ) : payable ? (
                          <Badge tone="primary">Payable</Badge>
                        ) : (
                          <span className="text-sm text-foreground-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-sm text-foreground-700">
                        {entry.deliverablesTotal > 0
                          ? `${entry.deliverablesDone}/${entry.deliverablesTotal}`
                          : '—'}
                      </td>
                      <td className="px-3 py-3 text-sm text-foreground-700">
                        {formatMoney(entry.rate, entry.currency)}
                      </td>
                      <td className="px-3 py-3">
                        {advance ? (
                          <>
                            <p className="text-sm font-medium text-secondary-900">
                              {formatMoney(balance, entry.currency)}
                            </p>
                            <span className="mt-1 flex">
                              <EarlyPayoutNotice
                                variant="inline"
                                advance={advance}
                                currency={entry.currency}
                                invoiceTotal={entry.rate}
                              />
                            </span>
                          </>
                        ) : (
                          <span className="text-sm text-foreground-500">Full rate</span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        {eligible ? (
                          <Badge tone="accent" icon="ri-check-line">
                            Eligible
                          </Badge>
                        ) : (
                          <Badge tone="outline" icon="ri-close-circle-line">
                            Not eligible
                          </Badge>
                        )}
                        <p className="mt-1 max-w-[220px] text-xs text-foreground-500">{reason}</p>
                      </td>
                      <td className="px-5 py-3 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {canReviewPayouts && (payable || processing) ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              icon="ri-check-double-line"
                              disabled={payingId === entry.id}
                              onClick={() => void markPaid(entry)}
                            >
                              {payingId === entry.id ? 'Marking…' : 'Mark paid'}
                            </Button>
                          ) : null}
                          <Button
                            size="sm"
                            variant="outline"
                            icon="ri-send-plane-line"
                            disabled={!eligible}
                            title={eligible ? undefined : `Can't pay yet: ${reason}`}
                            onClick={() =>
                              navigate(
                                `/payments/send?creator=${creator.id}&find=${encodeURIComponent(creator.handle?.includes('@') ? creator.handle : creator.name)}&cc=${encodeURIComponent(entry.id)}&ccStatus=${encodeURIComponent(entry.statusKey ?? '')}&campaign=${encodeURIComponent(campaign.name)}`,
                              )
                            }
                          >
                            Pay
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {roster.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-5 py-12 text-center text-sm text-foreground-500">
                      No payable creators on this campaign yet.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
          </div>

          <section className="h-fit rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-sm font-semibold text-foreground-950">
              Payment batch preview
            </h2>
            <dl className="mt-4 space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <dt className="text-foreground-500">Payable creators</dt>
                <dd className="font-medium text-foreground-900">{roster.length}</dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-foreground-500">Committed</dt>
                <dd className="font-medium text-foreground-900">
                  {formatMoney(payable, campaign.currency)}
                </dd>
              </div>
              {advancedTotal > 0 ? (
                <div className="flex items-center justify-between">
                  <dt className="inline-flex items-center gap-1.5 text-secondary-900">
                    <i className="ri-hand-coin-line" />
                    Early payout advanced
                  </dt>
                  <dd className="font-medium text-secondary-900">
                    − {formatMoney(advancedTotal, campaign.currency)}
                  </dd>
                </div>
              ) : null}
              <div className="flex items-center justify-between border-t border-background-100 pt-3">
                <dt className="text-foreground-500">Net balance to pay</dt>
                <dd className="font-heading text-base font-semibold text-foreground-950">
                  {formatMoney(netPayable, campaign.currency)}
                </dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-foreground-500">Budget remaining</dt>
                <dd className="font-heading text-base font-semibold text-accent-700">
                  {formatMoney(campaign.budget - payable, campaign.currency)}
                </dd>
              </div>
            </dl>

            {advancedTotal > 0 ? (
              <p className="mt-3 flex items-start gap-1.5 rounded-md border border-secondary-200 bg-secondary-50 px-3 py-2 text-xs leading-relaxed text-secondary-900">
                <span className="mt-0.5 w-3.5 h-3.5 flex items-center justify-center shrink-0">
                  <i className="ri-information-line" />
                </span>
                {advancedCount} creator{advancedCount === 1 ? '' : 's'} already drew an early payout.
                The batch settles the net {formatMoney(netPayable, campaign.currency)}, not the full{' '}
                {formatMoney(payable, campaign.currency)}.
              </p>
            ) : null}
            <div className="mt-4 space-y-2">
              <Button icon="ri-stack-line" className="w-full" onClick={() => navigate('/payments/bulk')}>
                Build payment batch
              </Button>
              <Button
                variant="outline"
                icon="ri-money-dollar-circle-line"
                className="w-full"
                onClick={() => navigate('/invoices')}
              >
                Review invoices
              </Button>
            </div>
          </section>
        </div>
      ) : null}

      <EditCampaignModal
        open={editOpen}
        campaign={campaign}
        saving={busy}
        error={editError}
        onClose={() => setEditOpen(false)}
        onSave={saveEdits}
      />

      <AddCreatorModal open={addOpen} saving={busy} error={addError} onClose={() => setAddOpen(false)} onAdd={addCreators} />

      <CreatorAiModal
        mode={aiEntry?.mode ?? null}
        campaignId={campaign.id}
        rosterId={aiEntry?.entry.id ?? ''}
        creatorName={aiEntry?.entry.creator?.name ?? 'this creator'}
        onClose={() => setAiEntry(null)}
        onVetted={(rosterId, result) => setVettings((current) => ({ ...current, [rosterId]: result }))}
      />

      <RespondModal
        entry={respondEntry}
        budgetPerCreator={campaign.budgetPerCreator ?? 0}
        currency={campaign.currency}
        saving={busy}
        error={respondError}
        onClose={() => setRespondEntry(null)}
        onRespond={respond}
      />

      <Modal
        open={rosterConfirm !== null}
        onClose={() => setRosterConfirm(null)}
        icon="ri-user-settings-line"
        title={rosterConfirm?.title ?? ''}
        subtitle={campaign.name}
        widthClassName="max-w-md"
        footer={
          <>
            <Button variant="outline" onClick={() => setRosterConfirm(null)}>
              Cancel
            </Button>
            <Button disabled={busy} onClick={confirmRoster}>
              {busy ? 'Working…' : rosterConfirm?.label}
            </Button>
          </>
        }
      >
        <p className="text-sm text-foreground-700">{rosterConfirm?.body}</p>
      </Modal>

      <Modal
        open={confirming !== null}
        onClose={() => setConfirming(null)}
        icon={
          {
            advance: 'ri-arrow-right-line',
            disable: 'ri-link-unlink-line',
            enable: 'ri-link-m',
            archive: 'ri-archive-line',
            reopen: 'ri-inbox-unarchive-line',
          }[confirming ?? 'advance']
        }
        title={
          {
            advance: nextStage?.label,
            disable: 'Disable the onboarding link?',
            enable: 'Re-enable the onboarding link?',
            archive: 'Archive this campaign?',
            reopen: 'Reopen this campaign?',
          }[confirming ?? 'advance']
        }
        subtitle={campaign.name}
        widthClassName="max-w-md"
        footer={
          <>
            <Button variant="outline" onClick={() => setConfirming(null)}>
              Cancel
            </Button>
            <Button disabled={busy} onClick={() => confirming && runAction(confirming)}>
              {busy ? 'Working…' : 'Confirm'}
            </Button>
          </>
        }
      >
        <p className="text-sm text-foreground-700">
          {
            {
              advance: `The campaign moves from ${campaign.status} to ${nextStage?.to}. Stages only move forward.`,
              disable: "Creators who open the link won't be able to join the campaign. Creators already on it aren't affected.",
              enable: 'Creators can join the campaign through the link again.',
              archive:
                "The campaign moves to Archived and its onboarding link stops working. Its creators, content and payments are kept, and you can reopen it later.",
              reopen:
                'The campaign goes back to Recruiting and its onboarding link works again. Move it on to Active when you are ready.',
            }[confirming ?? 'advance']
          }
        </p>
      </Modal>
    </div>
  );
}
